import { ACTIVE_URL } from '../config';
import { apiFetch } from './apiFetch';
import { extractErrorMessage } from '../utils/errorHandling';
const API_BASE_URL = `${ACTIVE_URL}/api/v1`;

export const authService = {
  async login(user_name: string, password: string) {
    const response = await fetch(`${API_BASE_URL}/auth/login/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ user_name, password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(extractErrorMessage(errorData, 'Login failed'));
    }

    return response.json(); // { access, refresh }
  },

  getAccessToken() {
    return localStorage.getItem('access_token');
  },

  getRefreshToken() {
    return localStorage.getItem('refresh_token');
  },

  async getMe() {
    const token = this.getAccessToken();
    const response = await apiFetch(`${API_BASE_URL}/auth/me/`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch user details');
    }

    return response.json(); // user object
  },

  async logout() {
    const refreshToken = this.getRefreshToken();
    const accessToken = this.getAccessToken();
    try {
      if (refreshToken && accessToken) {
        await apiFetch(`${API_BASE_URL}/auth/logout/`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh: refreshToken }),
        });
      }
    } catch (err) {
      console.error('Logout failed on backend:', err);
    } finally {
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    }
  },

  async changePassword(oldPassword: string, newPassword: string) {
    const token = this.getAccessToken();
    const response = await apiFetch(`${API_BASE_URL}/auth/change-password/`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ old_password: oldPassword, new_password: newPassword }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(extractErrorMessage(errorData, 'Password change failed'));
    }

    return response.json();
  },

  async verifyOtp(temp_token: string, otp_code: string) {
    const response = await fetch(`${API_BASE_URL}/auth/login/verify-otp/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ temp_token, otp_code }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(extractErrorMessage(errorData, 'OTP verification failed'));
    }

    return response.json();
  },

  async resendOtp(temp_token: string) {
    const response = await fetch(`${API_BASE_URL}/auth/login/resend-otp/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ temp_token }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(extractErrorMessage(errorData, 'Failed to resend OTP'));
    }

    return response.json();
  },

  async forgotPassword(email: string) {
    const response = await fetch(`${API_BASE_URL}/auth/forgot-password/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(extractErrorMessage(errorData, 'Failed to request password reset'));
    }

    return response.json();
  },

  async confirmResetPassword(uid: string, token: string, new_password: string) {
    const response = await fetch(`${API_BASE_URL}/auth/reset-password/confirm/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ uid, token, new_password }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(extractErrorMessage(errorData, 'Failed to reset password'));
    }

    return response.json();
  }
};
